import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DOCUMENT,
    Inject,
    OnDestroy,
    OnInit
} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {ActivatedRoute, NavigationStart, Router} from '@angular/router';
import {NgbDate} from '@ng-bootstrap/ng-bootstrap';
import {Actions} from '@ngrx/effects';
import {RoutingService, TranslationService} from '@spartacus/core';
import {LaunchDialogService} from '@spartacus/storefront';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged, filter, map} from 'rxjs/operators';

import {AimoCartList} from '../../model/cart.model';
import {AimoFacet, AimoTemplateFacet} from '../../model/product.model';
import {AimoActiveCartService} from '../../service/cart/aimo-active-cart.service';
import {AimoGtmService} from '../../service/gtm/aimo-gtm.service';
import {AimoUserService} from '../../service/user/aimo-user.service';
import {AimoNgbDateParserFormatter} from '../shared/datepicker/aimo-datepicker-parser-formatter';
import {openCloseSpinner} from '../shared/utils/spinner/aimo-spinner-utils';

import {ORDER_TEMPLATE} from './order-template-modal/aimo-order-template-layout.config';
import {AimoUxTypeService} from "../../service/general/aimo-ux-type.service";

@Component({
    selector: 'aimo-order-templates',
    templateUrl: './aimo-order-templates.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class AimoOrderTemplatesComponent implements OnInit, OnDestroy {
    cartList$ = new BehaviorSubject<AimoCartList>({carts: [], facets: []});
    searchForm: FormGroup;
    subscriptions: Subscription = new Subscription();
    selectedFacet: AimoTemplateFacet = {
        selected: true,
        code: 'all-facets',
    };

    sortEntries = [{code: 'name'}, {code: 'creationtime'}];

    hideNormalOrderTemplatesFromBasicUsers: boolean;
    customerAdmin: boolean;
    searchInput = new BehaviorSubject(undefined);

    // eslint-disable-next-line
    breadcrumbs$: Observable<any[]>;

    templateId$: Observable<string>;
    isMobileApp$: Observable<boolean>;

    constructor(
        protected launchDialogService: LaunchDialogService,
        protected userService: AimoUserService,
        protected activeCartService: AimoActiveCartService,
        protected ngbDateParserFormatter: AimoNgbDateParserFormatter,
        protected activatedRoute: ActivatedRoute,
        protected routingService: RoutingService,
        protected cdr: ChangeDetectorRef,
        private lTranslation: TranslationService,
        protected actions$: Actions,
        protected gtmService: AimoGtmService,
        @Inject(DOCUMENT) private document: Document,
        protected aimoUxTypeService: AimoUxTypeService,
        private router: Router,
    ) {
        this.isMobileApp$ = this.aimoUxTypeService.isMobileApp();
        this.router.events.pipe(
            filter((event: any) => event instanceof NavigationStart)
        ).subscribe((nav) => {
            if (nav.url.includes('reload=true')) {
                this.fetchTemplates();
            }
        });
    }

    ngOnInit(): void {
        // eslint-disable-next-line
        this.breadcrumbs$ = this.lTranslation.translate('aimo.ordertemplates.title').pipe(
            filter(Boolean),
            map((translation) => [{label: translation, link: '/ordertemplates'}]),
        );

        this.templateId$ = this.routingService.getRouterState().pipe(
            map((routingData) => {
                const id = routingData.state.context.id?.replace('/ordertemplates', '').replace('/', '');
                if (id == '') {
                    return undefined;
                }
                return id;
            }),
            distinctUntilChanged(),
        );
        this.setUserValues();
    }

    private setUserValues(): void {
        this.subscriptions.add(
            this.userService
                .get()
                .pipe(filter((user) => user !== undefined))
                .subscribe((user) => {
                    this.hideNormalOrderTemplatesFromBasicUsers = user.isHideNormalOrderTemplatesFromBasicUsers;
                    this.customerAdmin = user.customerAdmin;
                    this.initForm();
                    this.cdr.detectChanges();
                }),
        );
    }

    private initForm(): void {
        const searchTerm = this.activatedRoute.snapshot.queryParams?.searchTerm;
        const sortField = this.activatedRoute.snapshot.queryParams?.sortField;
        this.searchForm = new FormGroup({
            search: new FormControl(searchTerm),
            from: new FormControl(),
            to: new FormControl(),
            sortField: new FormControl(sortField ?? this.sortEntries[0].code),
            hideNormalOrderTemplatesFromBasicUsers: new FormControl(this.hideNormalOrderTemplatesFromBasicUsers),
        });
        this.subscriptions.add(
            this.launchDialogService.dialogClose.subscribe((next) => {
                // Fetch templates on dialog close
                if (next) {
                    this.fetchTemplates();
                    this.cdr.detectChanges();
                }
            }),
        );

        this.subscriptions.add(
            this.templateId$.subscribe(id => {
                this.fetchTemplates();
            })
        );

        this.subscriptions.add(
            this.searchInput.pipe(
                distinctUntilChanged(),
                debounceTime(300),
                filter((term) => term !== undefined),
            ).subscribe((term) => this.fetchTemplates()),
        );
    }

    toggleFacet(facet: AimoFacet): void {
        if (this.selectedFacet?.code === facet.code) {
            // Deselect if clicking selected facet
            this.selectedFacet = undefined;
        } else {
            this.selectedFacet = facet;
        }
        this.fetchTemplates();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    openCreateTemplateModal(): void {
        this.launchDialogService.closeDialog(null);
        this.launchDialogService.openDialogAndSubscribe(ORDER_TEMPLATE, undefined, {createNew: true});
    }

    search(): void {
        this.searchInput.next(this.searchForm.controls['search'].value);
    }

    fetchTemplates(): void {
        this.activeCartService
            .getOrderTemplates(
                this.selectedFacet?.code !== 'all-facets' ? this.selectedFacet?.code : undefined,
                this.searchForm.controls['search'].value,
                {
                    field: this.searchForm.controls['sortField'].value,
                    descending: this.searchForm.controls['sortField'].value === 'creationtime'
                }
            )
            .forEach((next) => {
                this.cartList$.next(next);
                openCloseSpinner(this.document, false);
            });
    }

    convertDate(d: string): NgbDate {
        return d ? this.ngbDateParserFormatter.parse(new Date(d)) : null;
    }

    getFacetHits(facet: AimoFacet): number {
        return facet.values.filter((a) => a.code === 'hits')[0]?.count;
    }

    setHideNormalOrderTemplatesFromBasicUsers(): void {
        this.userService.setHideNormalOrderTemplatesFromBasicUsers(
            this.searchForm.controls.hideNormalOrderTemplatesFromBasicUsers.value,
        );
    }

    publicTemplatesVisible(): boolean {
        return this.customerAdmin !== undefined && (this.customerAdmin || !this.hideNormalOrderTemplatesFromBasicUsers);
    }

    pushGtmEvent(event: string): void {
        this.gtmService.pushEvent(event);
    }
}
